import { useQuery } from '@tanstack/react-query';
import {
  Clock,
  MapPin,
  TrendingUp,
  Calendar,
  Loader2,
  ToggleRight,
  AlertCircle,
} from 'lucide-react';
import { fleetService } from '../services/fleetService';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

export default function PeakHours() {
  // Fetch peak hours for fleet's region
  const { data, isLoading, error } = useQuery({
    queryKey: ['fleetPeakHours'],
    queryFn: () => fleetService.getPeakHours().then((res) => res.data),
  });

  const region = data?.region;
  const peakHours = data?.peakHours || [];

  // True only when the rule is enabled by admin AND the current time falls in its window
  const isCurrentlyPeak = (peakHour) => {
    if (!peakHour?.is_active) return false;
    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.toTimeString().substring(0, 5);

    return (
      peakHour.days_of_week?.includes(currentDay) &&
      peakHour.start_time?.substring(0, 5) <= currentTime &&
      peakHour.end_time?.substring(0, 5) >= currentTime
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 lg:p-8">
        <div className="bg-danger-light dark:bg-danger-dark rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-danger mx-auto mb-3" />
          <p className="text-danger-dark dark:text-danger-light font-medium">
            Failed to load peak hours
          </p>
          <p className="text-sm text-text-secondary dark:text-text-secondary-dark mt-1">
            {error.response?.data?.message || error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-text dark:text-text-dark">
          Peak Hours
        </h1>
        <p className="text-text-secondary dark:text-text-secondary-dark mt-1">
          Peak hour pricing rules for your region
        </p>
      </div>

      {/* Region Info */}
      {region ? (
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl border border-primary/20 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-text-secondary dark:text-text-secondary-dark">Your Region</p>
              <p className="text-lg font-bold text-text dark:text-text-dark">
                {region.display_name}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-warning-light dark:bg-warning-dark rounded-xl border border-warning/20 p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-warning" />
            <div>
              <p className="text-sm font-medium text-warning-dark dark:text-warning-light">
                No region assigned
              </p>
              <p className="text-xs text-text-secondary dark:text-text-secondary-dark">
                Contact your administrator to assign a region to your fleet.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {peakHours.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-surface dark:bg-surface-dark rounded-xl border border-border dark:border-border-dark p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-text-secondary dark:text-text-secondary-dark">Peak Rules</p>
                <p className="text-xl font-bold text-text dark:text-text-dark">{peakHours.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface dark:bg-surface-dark rounded-xl border border-border dark:border-border-dark p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-sm text-text-secondary dark:text-text-secondary-dark">Active rules</p>
                <p className="text-xl font-bold text-text dark:text-text-dark">
                  {peakHours.filter((ph) => ph.is_active).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-surface dark:bg-surface-dark rounded-xl border border-border dark:border-border-dark p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <ToggleRight className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-text-secondary dark:text-text-secondary-dark">In effect now</p>
                <p className="text-xl font-bold text-text dark:text-text-dark">
                  {peakHours.filter(isCurrentlyPeak).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Peak Hours List */}
      {peakHours.length === 0 ? (
        <div className="bg-surface dark:bg-surface-dark rounded-xl border border-border dark:border-border-dark p-12 text-center">
          <Clock className="w-12 h-12 mx-auto mb-4 text-text-tertiary dark:text-text-tertiary-dark opacity-50" />
          <p className="text-lg font-medium text-text-secondary dark:text-text-secondary-dark">
            No peak hours configured
          </p>
          <p className="text-sm text-text-tertiary dark:text-text-tertiary-dark mt-1">
            {region
              ? 'No peak hour rules have been set for your region yet.'
              : 'Assign a region to your fleet to view peak hours.'}
          </p>
        </div>
      ) : (
        <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-secondary dark:bg-surface-secondary-dark">
                <tr>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                    Peak Hour
                  </th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                    Time Range
                  </th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                    Days
                  </th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                    Pricing
                  </th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                    Validity
                  </th>
                  <th className="px-4 lg:px-6 py-4 text-left text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                    Rule status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border dark:divide-border-dark">
                {peakHours.map((peakHour) => {
                  const isPeak = isCurrentlyPeak(peakHour);
                  const ruleEnabled = !!peakHour.is_active;
                  return (
                    <tr
                      key={peakHour.id}
                      className={`transition-colors ${
                        isPeak
                          ? 'bg-warning/5 dark:bg-warning/10'
                          : 'hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark'
                      } ${!ruleEnabled ? 'opacity-80' : ''}`}
                    >
                      {/* Name */}
                      <td className="px-4 lg:px-6 py-4">
                        <div className="flex items-center gap-2">
                          {isPeak && (
                            <span className="flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-warning opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-warning"></span>
                            </span>
                          )}
                          <div>
                            <div className="text-sm font-medium text-text dark:text-text-dark">
                              {peakHour.name}
                            </div>
                            {peakHour.description && (
                              <div className="text-xs text-text-tertiary dark:text-text-tertiary-dark mt-0.5">
                                {peakHour.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Time Range */}
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-sm text-text dark:text-text-dark">
                          <Clock className="w-3.5 h-3.5 text-text-tertiary dark:text-text-tertiary-dark" />
                          {formatTime(peakHour.start_time)} - {formatTime(peakHour.end_time)}
                        </div>
                      </td>

                      {/* Days */}
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-1">
                          {DAY_LABELS.map((label, idx) => (
                            <span
                              key={idx}
                              className={`w-7 h-7 flex items-center justify-center rounded text-xs font-medium ${
                                peakHour.days_of_week?.includes(idx)
                                  ? 'bg-primary text-white'
                                  : 'bg-surface-secondary dark:bg-surface-secondary-dark text-text-tertiary dark:text-text-tertiary-dark'
                              }`}
                            >
                              {label.charAt(0)}
                            </span>
                          ))}
                        </div>
                      </td>

                      {/* Pricing */}
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div>
                          <span className="text-sm font-bold text-warning">
                            {peakHour.multiplier}x
                          </span>
                          {parseFloat(peakHour.bonus_amount) > 0 && (
                            <span className="text-xs text-text-tertiary dark:text-text-tertiary-dark ml-1">
                              + ${peakHour.bonus_amount} bonus
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Validity */}
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-text-secondary dark:text-text-secondary-dark">
                          {peakHour.effective_from || peakHour.effective_until ? (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>
                                {peakHour.effective_from || 'Start'} - {peakHour.effective_until || 'Ongoing'}
                              </span>
                            </div>
                          ) : (
                            <span className="text-text-tertiary dark:text-text-tertiary-dark">Always</span>
                          )}
                        </div>
                      </td>

                      {/* Rule status: admin on/off + whether this window applies right now */}
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        {!ruleEnabled ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-surface-secondary dark:bg-surface-secondary-dark text-text-tertiary dark:text-text-tertiary-dark">
                            <span className="w-1.5 h-1.5 rounded-full bg-text-tertiary dark:bg-text-tertiary-dark" />
                            Inactive
                          </span>
                        ) : isPeak ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-warning-light dark:bg-warning-dark text-warning-dark dark:text-warning-light">
                            <span className="w-1.5 h-1.5 rounded-full bg-warning animate-pulse" />
                            In effect now
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-success-light dark:bg-success-dark text-success">
                            <span className="w-1.5 h-1.5 rounded-full bg-success" />
                            Active (off-peak)
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
